import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';

import type { FilterValues, FilterFieldConfig, ListFilterPayload } from '@/types/filters';
import type { TableSearchParams } from '@utils/tabHelpers';
import { serializeFilters, deserializeFilters } from '@utils/tabHelpers';

// ============================================
// TYPES
// ============================================

export interface UseTableFiltersOptions {
  /** Route path for navigate() — e.g., '/w/dashboard' */
  routeTo: string;
  /** Raw `filters` string from URL search params (via Route.useSearch()) */
  filtersParam: string | undefined;
  /** Filter field configs — needed for API payload transformation */
  fields: FilterFieldConfig[];
}

export interface UseTableFiltersReturn {
  /** Current filter values parsed from URL */
  filters: FilterValues;
  /** Whether any filters are active */
  hasFilters: boolean;
  /** Count of active filter fields */
  activeFilterCount: number;
  /** Whether the filter panel is open */
  isPanelOpen: boolean;
  /** Toggle filter panel visibility */
  togglePanel: () => void;
  /** Close filter panel */
  closePanel: () => void;
  /** Apply filter values from the panel (replaces all) */
  applyFilters: (values: FilterValues) => void;
  /** Remove a single filter by fieldKey */
  removeFilter: (fieldKey: string) => void;
  /** Clear all filters */
  clearAllFilters: () => void;
  /** Structured body payload for POST .../list endpoints */
  filterPayload: ListFilterPayload | undefined;
}

// ============================================
// HELPERS
// ============================================

/** Append T00:00:00 to a YYYY-MM-DD date string if it doesn't already have a time component */
function toDateTimeString(date: string): string {
  if (date.includes('T')) return date;
  return `${date}T00:00:00`;
}

/**
 * Convert UI FilterValues + field configs into the API `filters` object.
 *
 * - text fields → { like: "value" }
 * - select/toggle/boolean → direct value
 * - multi-select → direct value (array of strings)
 * - date-range → { gte?, lte? }
 */
function buildApiFilters(
  values: FilterValues,
  fields: FilterFieldConfig[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === '') continue;

    const field = fields.find((f) => f.fieldKey === key);
    const fieldType = field?.type ?? 'text';

    switch (fieldType) {
      case 'text':
        result[key] = { like: value as string };
        break;
      case 'select':
      case 'toggle':
        result[key] = value;
        break;
      case 'multi-select':
        if (Array.isArray(value) && value.length > 0) {
          result[key] = { in: value };
        }
        break;
      case 'date-range':
        if (typeof value === 'object' && !Array.isArray(value)) {
          const range = value as { gte?: string; lte?: string };
          const dateFilter: Record<string, string> = {};
          if (range.gte) dateFilter.gte = toDateTimeString(range.gte);
          if (range.lte) dateFilter.lte = toDateTimeString(range.lte);
          if (Object.keys(dateFilter).length > 0) {
            result[key] = dateFilter;
          }
        }
        break;
      default:
        result[key] = value;
    }
  }

  return result;
}

// ============================================
// HOOK
// ============================================

/**
 * Manages table filter state with URL persistence.
 *
 * Filter values are serialized as a JSON string in the `filters` URL search param.
 * Produces a structured `ListFilterPayload` body for POST .../list API calls.
 */
export function useTableFilters({
  routeTo,
  filtersParam,
  fields,
}: UseTableFiltersOptions): UseTableFiltersReturn {
  const navigate = useNavigate();
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Parse filters from URL
  const filters = useMemo(() => deserializeFilters(filtersParam), [filtersParam]);
  const activeFilterCount = useMemo(() => Object.keys(filters).length, [filters]);
  const hasFilters = activeFilterCount > 0;

  const togglePanel = useCallback(() => setIsPanelOpen((prev) => !prev), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);

  // Navigate with updated filters, resetting page to 0
  const navigateWithFilters = useCallback(
    (newFilters: FilterValues) => {
      void navigate({
        to: routeTo,
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          filters: serializeFilters(newFilters),
        }),
        replace: true,
      });
    },
    [navigate, routeTo],
  );

  const applyFilters = useCallback(
    (values: FilterValues) => {
      // Strip empty values
      const cleaned: FilterValues = {};
      for (const [key, value] of Object.entries(values)) {
        if (value === '' || value === undefined) continue;
        if (Array.isArray(value) && value.length === 0) continue;
        if (typeof value === 'object' && !Array.isArray(value)) {
          const range = value as { gte?: string; lte?: string };
          if (!range.gte && !range.lte) continue;
        }
        cleaned[key] = value;
      }
      navigateWithFilters(cleaned);
    },
    [navigateWithFilters],
  );

  const removeFilter = useCallback(
    (fieldKey: string) => {
      const next = { ...filters };
      delete next[fieldKey];
      navigateWithFilters(next);
    },
    [filters, navigateWithFilters],
  );

  const clearAllFilters = useCallback(() => {
    navigateWithFilters({});
  }, [navigateWithFilters]);

  // Build structured payload for POST .../list endpoints
  const filterPayload = useMemo<ListFilterPayload | undefined>(() => {
    if (!hasFilters) return undefined;
    const apiFilters = buildApiFilters(filters, fields);
    if (Object.keys(apiFilters).length === 0) return undefined;
    return { filters: apiFilters };
  }, [filters, fields, hasFilters]);

  return {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  };
}
